import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DateTile,
  EmptyState,
  Field,
  Icon,
  Input,
  PageHeader,
  SaveBar,
  Textarea,
  useToast,
} from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import { PageLoader } from '../../shared/ui/PageLoader.js';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';
import type { BadgeTone } from '@acs/ui';
import type { AppointmentDoc, ClientDoc } from '../../shared/api/converters.js';
import { formatShortDate, formatTime } from '../../shared/lib/format.js';
import {
  useClient,
  useClientAppointments,
  useDeleteClient,
  useRevokeClientConsent,
  useUpdateClient,
} from './hooks.js';
import { parseClientParam } from './routes.js';

/**
 * One client at a glance (design template — Portal · Clients, detail view):
 * identity and stats on the left, their booking history on the right.
 */
export function ClientDetailPage() {
  const business = useActiveBusiness();
  const { e164: rawParam } = useParams<{ e164: string }>();
  const e164 = parseClientParam(rawParam);
  const navigate = useNavigate();
  const { data: client, isLoading } = useClient(business.id, e164);
  const { data: appointments } = useClientAppointments(business.id, e164);
  const del = useDeleteClient(business.id);
  const { toast } = useToast();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { t, tag } = useI18n();

  const listPath = `/b/${business.id}/clients`;

  if (isLoading) {
    return <PageLoader />;
  }

  if (!client) {
    return (
      <EmptyState
        icon={<Icon name="users" size={24} />}
        iconTone="neutral"
        title={t('clients.gone.title')}
        description={t('clients.gone.description')}
        actions={<Button onClick={() => navigate(listPath)}>{t('clients.backToClients')}</Button>}
      />
    );
  }

  return (
    <>
      <PageHeader
        back={
          <Link to={listPath} className="tc-btn tc-btn--link tc-btn--sm">
            <Icon name="arrow-left" size={16} /> {t('clients.allClients')}
          </Link>
        }
        leading={<Avatar name={client.name} size="lg" />}
        title={client.name}
        subtitle={<span className="mono">{client.phone}</span>}
        actions={
          <Button variant="danger" onClick={() => setConfirmDelete(true)} loading={del.isPending}>
            {t('clients.delete.confirm')}
          </Button>
        }
      />

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
        <StatsRow client={client} timezone={business.timezone} locale={tag} />
        <ProfileCard businessId={business.id} e164={e164} client={client} />
        <HistoryCard appointments={appointments ?? []} timezone={business.timezone} locale={tag} />
      </div>

      {confirmDelete && (
        <ConfirmDialog
          tone="danger"
          title={t('clients.delete.title', { name: client.name })}
          description={t('clients.delete.description')}
          icon={<Icon name="trash" size={24} />}
          confirmLabel={t('clients.delete.confirm')}
          loading={del.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() =>
            del.mutate(e164, {
              onSuccess: () => {
                toast({ title: t('clients.deleted', { name: client.name }), tone: 'success' });
                navigate(listPath);
              },
            })
          }
        />
      )}
    </>
  );
}

/** The three numbers worth knowing before you pick up: visits, since, usual. */
function StatsRow({ client, timezone, locale }: { client: ClientDoc; timezone: string; locale: string }) {
  const { t } = useI18n();
  const since = client.createdAt ? formatShortDate(client.createdAt, timezone, locale) : '—';
  const usual = client.recentServices[0]?.serviceName ?? '—';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
      <div className="tc-card tc-stat">
        <div className="tc-stat__label">{t('clients.stat.bookings')}</div>
        <div className="tc-stat__value">{client.bookingsCount}</div>
      </div>
      <div className="tc-card tc-stat">
        <div className="tc-stat__label">{t('clients.stat.since')}</div>
        <div className="tc-stat__value" style={{ fontSize: 20 }}>{since}</div>
      </div>
      <div className="tc-card tc-stat">
        <div className="tc-stat__label">{t('clients.stat.usualService')}</div>
        <div className="tc-stat__value" style={{ fontSize: 20 }}>{usual}</div>
      </div>
      <div className="tc-card tc-stat">
        <div className="tc-stat__label">{t('clients.stat.source')}</div>
        <div className="tc-stat__value" style={{ fontSize: 20 }}>
          {t(client.source === 'manual' ? 'clients.source.manual' : 'clients.source.call')}
        </div>
      </div>
    </div>
  );
}

function ProfileCard({ businessId, e164, client }: { businessId: string; e164: string; client: ClientDoc }) {
  const { t } = useI18n();
  const update = useUpdateClient(businessId, e164);
  const { toast } = useToast();
  const [name, setName] = useState(client.name);
  const [notes, setNotes] = useState(client.notes);

  // Keep local edits in sync when the live doc changes underneath (another tab).
  useEffect(() => setName(client.name), [client.name]);
  useEffect(() => setNotes(client.notes), [client.notes]);

  const dirty = name.trim() !== client.name || notes !== client.notes;
  const nameValid = name.trim().length >= 1 && name.trim().length <= 120;

  const save = () => {
    if (!nameValid || !dirty) return;
    update.mutate(
      { name: name.trim(), notes },
      { onSuccess: () => toast({ title: t('clients.saved'), tone: 'success' }) },
    );
  };

  const reset = () => {
    setName(client.name);
    setNotes(client.notes);
  };

  return (
    <Card title={t('clients.profile')}>
      <Field label={t('clients.name')} error={nameValid ? undefined : t('clients.nameRequired')}>
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
      </Field>
      <Field label={t('clients.notes')} hint={t('clients.notesHint')}>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={2000} />
      </Field>
      {dirty && (
        <SaveBar
          saveLabel={t('common.saveChanges')}
          discardLabel={t('common.discard')}
          regionLabel={t('ui.unsavedChanges')}
          message={t('common.unsavedChanges')}
          saving={update.isPending}
          onSave={save}
          onDiscard={reset}
        />
      )}
      {update.isError && <p className="field-error">{t('clients.saveFailed')}</p>}

      <ConsentRow businessId={businessId} client={client} />
    </Card>
  );
}

/**
 * Messaging consent (doc 21 §5), and the one action an owner may take on it.
 *
 * The button only ever REVOKES. Consent belongs to the customer — an owner
 * granting it from here would be manufacturing it — so the counterpart to this
 * row is the assistant asking on a call, not a second button. Revoking is
 * permanent and silences transactional messages too, which is why the label
 * says "all messages" rather than "reminders".
 */
function ConsentRow({ businessId, client }: { businessId: string; client: ClientDoc }) {
  const { t } = useI18n();
  const revoke = useRevokeClientConsent(businessId, client.phone);
  const { toast } = useToast();
  const revoked = client.reminders.revokedAt !== null;

  const tone: Record<string, BadgeTone> = {
    granted: 'success',
    denied: 'neutral',
    revoked: 'danger',
    unknown: 'neutral',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
      <span className="field-hint">{t('clients.consent')}</span>
      <Badge tone={tone[client.reminders.status] ?? 'neutral'}>
        {t(`clients.consent.${client.reminders.status}` as TranslationKey)}
      </Badge>
      {!revoked && (
        <Button
          variant="ghost"
          size="sm"
          loading={revoke.isPending}
          onClick={() =>
            revoke.mutate(undefined, {
              onSuccess: () => toast({ title: t('clients.consentRevoked'), tone: 'success' }),
            })
          }
        >
          {t('clients.consentRevoke')}
        </Button>
      )}
    </div>
  );
}

function HistoryCard({
  appointments,
  timezone,
  locale,
}: {
  appointments: AppointmentDoc[];
  timezone: string;
  locale: string;
}) {
  const { t } = useI18n();
  const month = new Intl.DateTimeFormat(locale, { timeZone: timezone, month: 'short' });
  const day = new Intl.DateTimeFormat(locale, { timeZone: timezone, day: '2-digit' });

  return (
    <Card title={t('clients.history')}>
      {appointments.length === 0 ? (
        <EmptyState
          borderless
          icon={<Icon name="calendar" size={24} />}
          iconTone="neutral"
          title={t('clients.noAppointments')}
          description={t('clients.noAppointmentsDescription')}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {appointments.map((a) => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <DateTile
                day={day.format(a.start)}
                month={month.format(a.start)}
                tone={a.status === 'cancelled' ? 'neutral' : 'primary'}
                label={formatShortDate(a.start, timezone, locale)}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{a.serviceName}</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {formatTime(a.start, timezone, locale)}
                  {a.staffName ? ` · ${a.staffName}` : ''}
                </div>
              </div>
              <Badge
                dot
                tone={a.status === 'confirmed' ? 'success' : a.status === 'cancelled' ? 'neutral' : 'warning'}
              >
                {t(`booking.status.${a.status}` as TranslationKey)}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
